import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  getDocs,
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebase';
import { toast } from 'react-toastify';
import { imageOptimizationService } from './imageOptimizationService';
import FirestoreWrapper from '../utils/firestoreWrapper';

// Interfaces pour les données
export interface RestaurantInfo {
  id: string;
  name: string;
  description: string;
  address: string;
  phone: string;
  email: string;
  website?: string;
  googleMapsLink?: string;
  logoUrl?: string | null;
  photoUrl?: string | null;
  openingHours: {
    [key: string]: { open: string; close: string; closed: boolean };
  };
  ownerId: string;
  createdAt: any;
  updatedAt: any;
  isActive: boolean;
  // Configuration du template de menu
  menuTemplate?: 'italian' | 'fastfood' | 'snack' | 'futuristic' | 'bistronomic' | null;
  // Configuration des avis externes
  googlePlaceId?: string;
  uberIntegration?: {
    status: 'pending' | 'active' | 'disabled';
    storeId?: string;
  };
  deliverooIntegration?: {
    status: 'pending' | 'active' | 'disabled';
    restaurantId?: string;
  };
}

export interface MenuCategory {
  id: string;
  name: string;
  description?: string;
  order: number;
  createdAt: any;
  updatedAt: any;
}

export interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  categoryId: string;
  imageUrl?: string;
  isAvailable: boolean;
  allergens: string[];
  createdAt: any;
  updatedAt: any;
}

export interface Review {
  id: string;
  customerName: string;
  rating: number;
  comment: string;
  date: any;
  isVerified: boolean;
  source: 'manual' | 'google';
  createdAt: any;
}

class RestaurantService {
  // Gestion des informations du restaurant
  async createRestaurant(restaurantData: Omit<RestaurantInfo, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const restaurantRef = doc(collection(db, 'restaurants'));
      const restaurantId = restaurantRef.id;
      
      const newRestaurant: RestaurantInfo = {
        ...restaurantData,
        id: restaurantId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true
      };
      
      await setDoc(restaurantRef, newRestaurant);
      return restaurantId;
    } catch (error: any) {
      toast.error('Erreur lors de la création du restaurant');
      throw new Error(`Erreur création restaurant: ${error.message}`);
    }
  }

  async getRestaurant(restaurantId: string): Promise<RestaurantInfo | null> {
    try {
      const restaurantRef = FirestoreWrapper.docRef('restaurants', restaurantId);
      const restaurantSnap = await FirestoreWrapper.getDocument(restaurantRef, {
        maxRetries: 3,
        showErrorToast: true
      });
      
      if (restaurantSnap.exists()) {
        return { id: restaurantSnap.id, ...restaurantSnap.data() } as RestaurantInfo;
      }
      return null;
    } catch (error: any) {
      throw new Error(`Erreur récupération restaurant: ${error.message}`);
    }
  }

  async updateRestaurant(restaurantId: string, updates: Partial<RestaurantInfo>): Promise<void> {
    try {
      const restaurantRef = FirestoreWrapper.docRef('restaurants', restaurantId);
      
      // Filtrer les valeurs undefined pour éviter les erreurs Firestore
      const cleanUpdates = Object.fromEntries(
        Object.entries(updates).filter(([_, value]) => value !== undefined)
      );
      
      await FirestoreWrapper.updateDocument(restaurantRef, {
        ...cleanUpdates,
        updatedAt: serverTimestamp()
      }, {
        maxRetries: 3,
        showErrorToast: true
      });
    } catch (error: any) {
      throw new Error(`Erreur mise à jour restaurant: ${error.message}`);
    }
  }

  // Gestion des images
  async uploadImage(file: File, path: string): Promise<string> {
    try {
      return await imageOptimizationService.uploadOptimizedImage(file, path);
    } catch (error: any) {
      toast.error('Erreur lors du téléchargement de l\'image');
      throw new Error(`Erreur upload image: ${error.message}`);
    }
  }

  async deleteImage(imageUrl: string): Promise<void> {
    try {
      await imageOptimizationService.deleteImage(imageUrl);
    } catch (error: any) {
      // Erreur lors de la suppression de l'image - continuer
    }
  }

  // Gestion des catégories de menu
  async createCategory(restaurantId: string, categoryData: Omit<MenuCategory, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const categoryRef = doc(collection(db, 'restaurants', restaurantId, 'menuCategories'));
      const categoryId = categoryRef.id;
      
      const newCategory: MenuCategory = {
        ...categoryData,
        id: categoryId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await setDoc(categoryRef, newCategory);
      return categoryId;
    } catch (error: any) {
      toast.error('Erreur lors de la création de la catégorie');
      throw new Error(`Erreur création catégorie: ${error.message}`);
    }
  }

  async getCategories(restaurantId: string): Promise<MenuCategory[]> {
    try {
      const categoriesQuery = query(
        collection(db, 'restaurants', restaurantId, 'menuCategories'),
        orderBy('order', 'asc')
      );
      
      const querySnapshot = await getDocs(categoriesQuery);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MenuCategory));
    } catch (error: any) {
      toast.error('Erreur lors de la récupération des catégories');
      throw new Error(`Erreur récupération catégories: ${error.message}`);
    }
  }

  async updateCategory(restaurantId: string, categoryId: string, updates: Partial<MenuCategory>): Promise<void> {
    try {
      const categoryRef = doc(db, 'restaurants', restaurantId, 'menuCategories', categoryId);
      await updateDoc(categoryRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error: any) {
      toast.error('Erreur lors de la mise à jour de la catégorie');
      throw new Error(`Erreur mise à jour catégorie: ${error.message}`);
    }
  }

  async deleteCategory(restaurantId: string, categoryId: string): Promise<void> {
    try {
      // Vérifier s'il y a des plats dans cette catégorie
      const itemsQuery = query(
        collection(db, 'restaurants', restaurantId, 'menuItems'),
        where('categoryId', '==', categoryId)
      );
      const itemsSnapshot = await getDocs(itemsQuery);
      
      if (!itemsSnapshot.empty) {
        throw new Error('Impossible de supprimer une catégorie contenant des plats');
      }
      
      const categoryRef = doc(db, 'restaurants', restaurantId, 'menuCategories', categoryId);
      await deleteDoc(categoryRef);
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de la suppression de la catégorie');
      throw error;
    }
  }

  // Gestion des plats
  async createMenuItem(restaurantId: string, itemData: Omit<MenuItem, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const itemRef = doc(collection(db, 'restaurants', restaurantId, 'menuItems'));
      const itemId = itemRef.id;
      
      // Filtrer les valeurs undefined pour éviter les erreurs Firestore
      const cleanItemData = Object.fromEntries(
        Object.entries(itemData).filter(([_, value]) => value !== undefined)
      );
      
      const newItem: any = {
        ...cleanItemData,
        id: itemId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await setDoc(itemRef, newItem);
      return itemId;
    } catch (error: any) {
      toast.error('Erreur lors de l\'ajout du plat');
      throw new Error(`Erreur création plat: ${error.message}`);
    }
  }

  async getMenuItems(restaurantId: string): Promise<MenuItem[]> {
    try {
      const itemsQuery = query(
        collection(db, 'restaurants', restaurantId, 'menuItems'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(itemsQuery);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MenuItem));
    } catch (error: any) {
      toast.error('Erreur lors de la récupération des plats');
      throw new Error(`Erreur récupération plats: ${error.message}`);
    }
  }

  async updateMenuItem(restaurantId: string, itemId: string, updates: Partial<MenuItem>, oldImageUrl?: string): Promise<void> {
    try {
      const itemRef = doc(db, 'restaurants', restaurantId, 'menuItems', itemId);
      
      // Si une nouvelle image est fournie et qu'il y avait une ancienne image, supprimer l'ancienne
      if (updates.imageUrl && oldImageUrl && updates.imageUrl !== oldImageUrl) {
        await this.deleteImage(oldImageUrl);
      }
      
      // Filtrer les valeurs undefined pour éviter les erreurs Firestore
      const cleanUpdates = Object.fromEntries(
        Object.entries(updates).filter(([_, value]) => value !== undefined)
      );
      
      await updateDoc(itemRef, {
        ...cleanUpdates,
        updatedAt: serverTimestamp()
      });
    } catch (error: any) {
      toast.error('Erreur lors de la mise à jour du plat');
      throw new Error(`Erreur mise à jour plat: ${error.message}`);
    }
  }

  async deleteMenuItem(restaurantId: string, itemId: string, imageUrl?: string): Promise<void> {
    try {
      // Supprimer l'image si elle existe
      if (imageUrl) {
        await this.deleteImage(imageUrl);
      }
      
      const itemRef = doc(db, 'restaurants', restaurantId, 'menuItems', itemId);
      await deleteDoc(itemRef);
    } catch (error: any) {
      toast.error('Erreur lors de la suppression du plat');
      throw new Error(`Erreur suppression plat: ${error.message}`);
    }
  }

  // Gestion des avis
  async createReview(restaurantId: string, reviewData: Omit<Review, 'id' | 'createdAt'>): Promise<string> {
    try {
      const reviewRef = doc(collection(db, 'restaurants', restaurantId, 'avis'));
      const reviewId = reviewRef.id;
      
      const newReview: Review = {
        ...reviewData,
        id: reviewId,
        createdAt: serverTimestamp()
      };
      
      await setDoc(reviewRef, newReview);
      toast.success('Avis ajouté avec succès');
      return reviewId;
    } catch (error: any) {
      toast.error('Erreur lors de l\'ajout de l\'avis');
      throw new Error(`Erreur création avis: ${error.message}`);
    }
  }

  async getReviews(restaurantId: string): Promise<Review[]> {
    // getReviews appelé
    try {
      // Récupérer les avis manuels depuis Firestore
      const reviewsQuery = query(
        collection(db, 'restaurants', restaurantId, 'avis'),
        orderBy('date', 'desc')
      );
      
      const querySnapshot = await getDocs(reviewsQuery);
      const manualReviews = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Review));
      // Avis manuels trouvés
      
      // Récupérer les avis Google si configuré
      const restaurant = await this.getRestaurant(restaurantId);
      let googleReviews: Review[] = [];
      
      if (restaurant?.googlePlaceId) {
        try {
          // Google Place ID trouvé
          // Import dynamique pour éviter les erreurs de compilation
          const { googlePlacesService } = await import('./googlePlacesService');
          googleReviews = await googlePlacesService.getGoogleReviews(restaurant.googlePlaceId);
          // Avis Google récupérés
        } catch (error) {
          // Erreur lors de la récupération des avis Google - continuer
        }
      } else {
        // Aucun Google Place ID configuré
      }
      
      // Combiner et trier tous les avis par date
      const allReviews = [...manualReviews, ...googleReviews];
      // Total avis retournés
      return allReviews.sort((a, b) => {
        const dateA = a.date.seconds || a.date;
        const dateB = b.date.seconds || b.date;
        return dateB - dateA;
      });
    } catch (error: any) {
      toast.error('Erreur lors de la récupération des avis');
      throw new Error(`Erreur récupération avis: ${error.message}`);
    }
  }

  async deleteReview(restaurantId: string, reviewId: string): Promise<void> {
    try {
      const reviewRef = doc(db, 'restaurants', restaurantId, 'avis', reviewId);
      await deleteDoc(reviewRef);
    } catch (error: any) {
      toast.error('Erreur lors de la suppression de l\'avis');
      throw new Error(`Erreur suppression avis: ${error.message}`);
    }
  }

  // Génération du menu public
  async getPublicMenu(restaurantId: string): Promise<{ restaurant: RestaurantInfo; categories: MenuCategory[]; items: MenuItem[] }> {
    try {
      const [restaurant, categories, items] = await Promise.all([
        this.getRestaurant(restaurantId),
        this.getCategories(restaurantId),
        this.getMenuItems(restaurantId)
      ]);
      
      if (!restaurant) {
        throw new Error('Restaurant non trouvé');
      }
      
      // Filtrer seulement les plats disponibles pour le menu public
      const availableItems = items.filter(item => item.isAvailable);
      
      return {
        restaurant,
        categories,
        items: availableItems
      };
    } catch (error: any) {
      throw new Error(`Erreur récupération menu public: ${error.message}`);
    }
  }

  // Batch operations pour optimiser les performances
  async batchUpdateMenuItems(restaurantId: string, updates: { id: string; data: Partial<MenuItem> }[]): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      updates.forEach(update => {
        const itemRef = doc(db, 'restaurants', restaurantId, 'menuItems', update.id);
        batch.update(itemRef, {
          ...update.data,
          updatedAt: serverTimestamp()
        });
      });
      
      await batch.commit();
      toast.success('Plats mis à jour avec succès');
    } catch (error: any) {
      toast.error('Erreur lors de la mise à jour des plats');
      throw new Error(`Erreur batch update: ${error.message}`);
    }
  }
}

export const restaurantService = new RestaurantService();